import { Link, useLocation } from 'react-router-dom'
import { LayoutDashboard, BookOpen, Bell, BarChart2, ClipboardList, Settings, ChevronDown, LogOut, Plus } from 'lucide-react'
import { useRef, useState, useEffect } from 'react'
import { useMsal } from '@azure/msal-react'
import { useNewATA } from '../lib/appContext'

const NAV_LINKS = [
  { to: '/',              label: 'Översikt',       icon: LayoutDashboard, exact: true },
  { to: '/journal',       label: 'Journal',        icon: BookOpen },
  { to: '/underrattelser',label: 'Underrättelser', icon: Bell },
  { to: '/specifikationer',label: 'Specifikationer',icon: BarChart2 },
  { to: '/prislista',     label: 'À-prislista',    icon: ClipboardList },
  { to: '/projekt',       label: 'Projekt',        icon: Settings },
]

interface NavigationProps {
  projectLine?: string | null
}

export default function Navigation({ projectLine }: NavigationProps) {
  const location = useLocation()
  const { instance, accounts } = useMsal()
  const { open: openNewATA } = useNewATA()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  const account  = accounts[0]
  const name     = account?.name ?? account?.username ?? 'Användare'
  const initials = name.split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase()

  const isActive = ({ to, exact }: { to: string; exact?: boolean }) =>
    exact ? location.pathname === to : location.pathname.startsWith(to)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  return (
    <>
      <nav className="topbar">
        <div className="topbar-brand">ÄTA Journal</div>

        {projectLine && (
          <div className="topbar-project">{projectLine}</div>
        )}

        <div className="topbar-nav">
          {NAV_LINKS.map(link => (
            <Link
              key={link.to}
              to={link.to}
              className={isActive(link) ? 'topbar-link active' : 'topbar-link'}
            >
              <link.icon size={14} />
              {link.label}
            </Link>
          ))}
        </div>

        <div className="topbar-actions">
          <button className="btn-primary btn-sm" onClick={openNewATA}>
            <Plus size={13} />
            Ny ÄTA
          </button>

          <div className="profile-menu" ref={menuRef}>
            <button className="profile-trigger" onClick={() => setMenuOpen(o => !o)}>
              <div className="profile-avatar">{initials}</div>
              <span className="profile-name">{name}</span>
              <ChevronDown size={13} className={menuOpen ? 'profile-chevron open' : 'profile-chevron'} />
            </button>

            {menuOpen && (
              <div className="profile-dropdown">
                <div className="profile-dropdown-header">
                  <div className="profile-avatar lg">{initials}</div>
                  <div>
                    <div className="profile-dropdown-name">{name}</div>
                    <div className="profile-dropdown-email">{account?.username}</div>
                  </div>
                </div>
                <div className="profile-dropdown-divider" />
                <button
                  className="profile-dropdown-item"
                  onClick={() => { setMenuOpen(false); instance.logoutRedirect() }}
                >
                  <LogOut size={14} />
                  Logga ut
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile bottom tabs */}
      <nav className="bottom-tabs">
        {NAV_LINKS.slice(0, 5).map(link => (
          <Link
            key={link.to}
            to={link.to}
            className={isActive(link) ? 'bottom-tab active' : 'bottom-tab'}
          >
            <link.icon size={20} />
            <span>{link.label.split('.')[0]}</span>
          </Link>
        ))}
      </nav>
    </>
  )
}
